import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Briefcase, CircleNotch, Lightning, Flame, WarningCircle,
  Star, ChartBar, Clock, CheckCircle, TrendUp, Package, Wrench, Eye
} from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="wp-card">
      <div className="flex items-center justify-between mb-1">
        <span className="metric-label">{label}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="metric-value" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-[#404040] font-mono-data mt-0.5">{sub}</div>}
    </div>
  );
}

function WorkerCard({ worker }) {
  const efficiency = worker.estimated_hours > 0
    ? Math.round((worker.total_hours / worker.estimated_hours) * 100)
    : 0;
  return (
    <div data-testid={`worker-card-${worker.name}`} className="wp-card animate-slide-up">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#002FA7] text-white rounded-sm flex items-center justify-center text-xs font-bold">
            {worker.name.split(" ").map(n => n[0]).join("").toUpperCase()}
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{worker.name}</h3>
            <span className="font-mono-data text-[10px] text-[#404040]">
              {worker.active_jobs} ACTIVE | {worker.completed_jobs} DONE
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-sm">{worker.total_hours}h</div>
          <span className="font-mono-data text-[10px] text-[#404040]">/ {worker.estimated_hours}h EST</span>
        </div>
      </div>
      {worker.estimated_hours > 0 && (
        <div className="flex items-center gap-2">
          <Progress value={Math.min(efficiency, 100)} className="flex-1 h-1.5" />
          <span className={`font-mono-data text-[10px] ${efficiency > 100 ? "text-[#FF5A00]" : "text-[#34C759]"}`}>
            {efficiency}%
          </span>
        </div>
      )}
    </div>
  );
}

function AdminJobRow({ job }) {
  const isElectrical = job.trade === "electrical";
  const statusColors = {
    pending: "#404040", in_progress: "#002FA7", inspection: "#FFCC00", completed: "#34C759"
  };
  return (
    <div
      data-testid={`admin-job-${job.id}`}
      className={`flex items-center gap-3 p-3 border-b border-[#E5E5E5] last:border-0 ${isElectrical ? "border-l-2 border-l-[#002FA7]" : "border-l-2 border-l-[#FF5A00]"}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {isElectrical ? <Lightning size={12} className="text-[#002FA7]" /> : <Flame size={12} className="text-[#FF5A00]" />}
          <span className="text-sm font-medium truncate">{job.title}</span>
        </div>
        <div className="text-[10px] font-mono-data text-[#404040] mt-0.5">
          {job.assigned_worker || "UNASSIGNED"} | {job.address}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="flex items-center gap-1.5">
          <Progress value={job.progress_percentage} className="w-16 h-1" />
          <span className="font-mono-data text-[10px] w-8">{job.progress_percentage}%</span>
        </div>
        <span className="font-mono-data text-[10px]" style={{ color: statusColors[job.status] || "#404040" }}>
          {job.status.replace("_", " ").toUpperCase()}
        </span>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewStats, setReviewStats] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, reviewRes] = await Promise.all([
        axios.get(`${API}/admin/overview`),
        axios.get(`${API}/reviews/stats`),
      ]);
      setData(overviewRes.data);
      setReviewStats(reviewRes.data);
    } catch {
      toast.error("Failed to load admin data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="admin-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Admin Dashboard
          </h1>
          <p className="text-[10px] font-mono-data text-[#404040]">MULTI-WORKER OVERVIEW</p>
        </div>
        <button
          data-testid="admin-refresh-btn"
          onClick={fetchData}
          className="px-3 py-1.5 text-xs font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
        <StatCard label="ACTIVE JOBS" value={data.active_jobs} icon={Briefcase} color="#002FA7" sub={`${data.total_jobs} total`} />
        <StatCard label="PENDING TASKS" value={data.pending_tasks} icon={Clock} color="#FF5A00" sub={`${data.total_tasks} total`} />
        <StatCard label="LOW STOCK" value={data.low_stock_count} icon={Package} color={data.low_stock_count > 0 ? "#FF5A00" : "#34C759"} />
        <StatCard label="AVG RATING" value={reviewStats?.avg_rating || "N/A"} icon={Star} color="#FFCC00" sub={`${reviewStats?.total || 0} reviews`} />
        <StatCard label="COMPLETED" value={data.completed_jobs} icon={CheckCircle} color="#34C759" />
        <StatCard label="INSPECTIONS" value={data.total_inspections} icon={Eye} color="#002FA7" />
        <StatCard label="MAINT. DUE" value={data.overdue_maintenance} icon={Wrench} color={data.overdue_maintenance > 0 ? "#FF5A00" : "#34C759"} />
        <StatCard label="WORKERS" value={data.workers.length} icon={Users} color="#002FA7" />
      </div>

      <Tabs defaultValue="workers">
        <TabsList className="grid w-full grid-cols-3 h-9 mb-4">
          <TabsTrigger value="workers" className="text-xs" data-testid="admin-workers-tab">Workers</TabsTrigger>
          <TabsTrigger value="jobs" className="text-xs" data-testid="admin-jobs-tab">All Jobs</TabsTrigger>
          <TabsTrigger value="alerts" className="text-xs" data-testid="admin-alerts-tab">
            Alerts {(data.low_stock_count + data.overdue_maintenance) > 0 && `(${data.low_stock_count + data.overdue_maintenance})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workers">
          {data.workers.length === 0 ? (
            <div className="wp-card text-center py-6 text-sm text-[#404040]">No workers assigned to jobs yet.</div>
          ) : (
            <div className="space-y-3">
              {data.workers.map((w, i) => (
                <div key={w.name} style={{ animationDelay: `${i * 0.05}s` }}>
                  <WorkerCard worker={w} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="jobs">
          <div className="wp-card p-0 max-h-[60vh] overflow-y-auto">
            {data.jobs.length === 0 ? (
              <p className="text-sm text-[#404040] p-4 text-center">No jobs yet.</p>
            ) : (
              data.jobs.map(job => <AdminJobRow key={job.id} job={job} />)
            )}
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-3">
            {data.low_stock_items?.length > 0 && (
              <div>
                <h3 className="font-mono-data text-[10px] text-[#FF5A00] mb-2 flex items-center gap-1">
                  <WarningCircle size={10} /> LOW STOCK ({data.low_stock_items.length})
                </h3>
                <div className="wp-card p-0">
                  {data.low_stock_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 border-b border-[#E5E5E5] last:border-0 text-xs">
                      <span>{item.name}</span>
                      <span className="font-mono-data text-[#FF5A00]">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.overdue_maintenance_items?.length > 0 && (
              <div>
                <h3 className="font-mono-data text-[10px] text-[#FFCC00] mb-2 flex items-center gap-1">
                  <Wrench size={10} /> MAINTENANCE DUE ({data.overdue_maintenance_items.length})
                </h3>
                <div className="wp-card p-0">
                  {data.overdue_maintenance_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 border-b border-[#E5E5E5] last:border-0 text-xs">
                      <span>{item.asset_name}</span>
                      <span className={`font-mono-data text-[10px] ${item.status === "overdue" ? "text-[#FF5A00]" : "text-[#FFCC00]"}`}>
                        {item.status.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.low_stock_count === 0 && data.overdue_maintenance === 0 && (
              <div className="wp-card text-center py-6 text-sm text-[#34C759]">
                <CheckCircle size={24} className="mx-auto mb-2" />
                All clear. No alerts.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
